import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Tabs, Tab, Button, Form } from "react-bootstrap";
import { useOrchidPro } from "../contexts/orchid-pro-context";
import { useUser } from "contexts/UserContext";
import OrchidProAvatar from "components/orchid-pro-avatar";
import ErrorMessage from "components/error-message";
import SuccessMessage from "components/success-message";
import ProTypeUpdate from "components/provider-intake/pro-type-update";
import AboutMe from "components/provider-intake/about-me/AboutMe";
import ProCredentials from "components/provider-intake/pro-credentials/ProCredentials";
import Service from "components/provider-intake/service";
import EducationTraining from "components/provider-intake/education-training";
import {
  SERVICE_FIELDS,
  EDUCATION_TRAINING_FIELDS,
  PRO_CREDENTIALS_FIELDS_PROFILE_SETTING,
  ABOUT_ME_PROFILE_SETTING,
} from "components/provider-intake/utility/providerInfo";
import postOrchidPro from "services/postOrchidPro";
import putOrchidProTypeById from "services/putOrchidProTypeById";
import createOrchidProType from "services/createOrchidProType";
import {
  getOrchidProNameAndAllGraduateDegreesString,
  getOrchidProTypeStringFromNewArr,
} from "utils/formatter/orchidPro";
import scrollToInvalidInput from "utils/scrollToInvalidInput";
import { filter_keys } from "utils/fn";
import { AmplitudePageName, track } from "utils/amplitude";
import styles from "./EditProfile.module.scss";
import { useTimeOnPageContext } from "contexts/time-on-page";
import useStreamSEOOptimizedBio from "hooks/useStreamSEOOptimizedBio";

const AMPLITUDE_PAGE_NAME: AmplitudePageName = "pro-settings-profile-page";

const tabs = [
  { eventKey: "about-me", title: "About Me", trackingKey: "about-me-tab" },
  {
    eventKey: "pro-credentials",
    title: "Professional Credentials",
    trackingKey: "professional-credentials-tab",
  },
  { eventKey: "service", title: "Service", trackingKey: "service-tab" },
  {
    eventKey: "education-training",
    title: "Education & Training",
    trackingKey: "education-&-training-tab",
  },
];

const getFormIdByTab = (tabId) => {
  switch (tabId) {
    case "about-me":
      return "aboutMe";
    case "pro-credentials":
      return "proCredentials";
    case "education-training":
      return "educationTraining";
    case "service":
      return "service";
    default:
      return "aboutMe";
  }
};

const EditProfile = () => {
  const { updateUserInfo } = useUser();
  const { orchidPro } = useOrchidPro();

  const navigate = useNavigate();
  const location = useLocation();

  const { activeTab } = useMemo(() => {
    const query = new URLSearchParams(location.search);
    return {
      activeTab: query.get("tab") || "",
    };
  }, [location.search]);

  const [tab, setTab] = useState(activeTab || "about-me");

  const [error, setError] = useState<any>();
  const [success, setSuccess] = useState<string>();
  const [changed, setChanged] = useState<boolean>();

  const { setPage } = useTimeOnPageContext();
  useEffect(() => setPage(AMPLITUDE_PAGE_NAME), [setPage]);

  const [state, setState] = useState({
    aboutMeValidated: false,
    aboutMeData: {},
    proCredentialsValidated: false,
    proCredentialsData: {},
    serviceValidated: false,
    serviceData: {},
    educationTrainingValidated: false,
    educationTrainingData: {},
  });

  useEffect(() => {
    if (!orchidPro) {
      return;
    }
    setState((state) => ({
      ...state,
      aboutMeData: filter_keys(orchidPro, ABOUT_ME_PROFILE_SETTING),
      proCredentialsData: filter_keys(
        orchidPro,
        PRO_CREDENTIALS_FIELDS_PROFILE_SETTING,
      ),
      serviceData: filter_keys(orchidPro, SERVICE_FIELDS),
      educationTrainingData: filter_keys(orchidPro, EDUCATION_TRAINING_FIELDS),
    }));
  }, [orchidPro]);

  const { bio, gptBusy, generateSEOOptimizedBioFromUserData } =
    useStreamSEOOptimizedBio();

  const getAIBio = useCallback(() => {
    setChanged(true);
    generateSEOOptimizedBioFromUserData({
      candidateData: JSON.stringify(orchidPro),
      setError: setError,
    });
  }, [generateSEOOptimizedBioFromUserData, orchidPro]);

  useEffect(() => {
    if (!gptBusy || !bio) return;

    setState((state) => ({
      ...state,
      aboutMeData: {
        ...state.aboutMeData,
        biography: bio,
      },
    }));
  }, [bio, gptBusy]);

  const fieldChangedCallback = useCallback((panelName, name, value, multi) => {
    setState((state) => ({
      ...state,
      [`${panelName}Data`]: multi
        ? {
            ...state[`${panelName}Data`],
            ...multi,
          }
        : {
            ...state[`${panelName}Data`],
            [name]: value,
          },
    }));
    setChanged(true);
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    setError(undefined);

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setState({ ...state, [`${form.name}Validated`]: true });
      setError("Please fill in required fields correctly.");
      scrollToInvalidInput(form);
      return;
    }

    const formData = state[`${form.name}Data`];
    if (form.name === "proCredentials") {
      for (let index = 0; index < formData.orchid_pro_types.length; index++) {
        if (!("orchid_pro_type_id" in formData.orchid_pro_types[index])) {
          try {
            await createOrchidProType(
              formData.orchid_pro_types[index].orchid_pro_type,
            );
          } catch (error) {
            setError(error);
            return;
          }
        } else {
          try {
            await putOrchidProTypeById(
              formData.orchid_pro_types[index].orchid_pro_type_id,
              formData.orchid_pro_types[index].orchid_pro_type,
            );
          } catch (error) {
            setError(error);
            return;
          }
        }
      }
    }

    try {
      await postOrchidPro(formData);
      setSuccess("Saved");
    } catch (error) {
      setError(error);
    } finally {
      setChanged(false);
      await updateUserInfo();
    }
  };

  return !orchidPro ? null : (
    <div className={styles["edit-profile"]}>
      {error && (
        <ErrorMessage
          show={true}
          onHide={() => setError(undefined)}
          errorMsg={error.toString()}
          className={styles["edit-profile__error-message"]}
        />
      )}
      {success && (
        <SuccessMessage
          show={true}
          onHide={() => setSuccess(undefined)}
          message={"Saved"}
          duration={1000}
        />
      )}
      <div className={styles["edit-profile__header"]}>
        <div className={"d-flex align-items-start"}>
          <OrchidProAvatar
            orchidPro={orchidPro}
            style={{ width: "4.5rem", height: "4.5rem" }}
          />
          <span className={styles["edit-profile__pro-info"]}>
            <div className={"d-flex align-items-center"}>
              <h4 className={styles["edit-profile__pro-name"]}>
                {getOrchidProNameAndAllGraduateDegreesString(orchidPro)}
              </h4>
              <Button
                className={"p-0 mb-1 ml-3"}
                variant={"link"}
                as={Link as any}
                target={"_blank"}
                rel={"noopener noreferrer"}
                to={`/professional-search/${orchidPro.custom_url}`}
                onClick={() =>
                  track(
                    AMPLITUDE_PAGE_NAME,
                    "view-profile-as-patient-button",
                    "click",
                  )
                }
              >
                View profile as patient
              </Button>
            </div>
            {!orchidPro.orchid_pro_types?.length ? null : (
              <div className={styles["edit-profile__pro-type"]}>
                {getOrchidProTypeStringFromNewArr(orchidPro)}
              </div>
            )}
          </span>
        </div>

        <Tabs
          activeKey={tab}
          onSelect={(s) => {
            setTab(s || "about-me");
            navigate(
              `/pro-dashboard/settings/my-profile?tab=${s || "about-me"}`,
              { replace: true }
            );
          }}
          className={styles["edit-profile__tabs"]}
        >
          {tabs.map(({ eventKey, title, trackingKey }) => (
            <Tab
              key={eventKey}
              eventKey={eventKey}
              title={title}
              onEnter={() => track(AMPLITUDE_PAGE_NAME, trackingKey, "click")}
            />
          ))}
        </Tabs>
      </div>

      {tab === "about-me" && (
        <div className={styles["edit-profile__body"]}>
          <Form
            noValidate
            name="aboutMe"
            validated={state.aboutMeValidated}
            onSubmit={handleSubmit}
            id="aboutMe"
          >
            <AboutMe
              data={state.aboutMeData}
              fieldChangedCallback={(name, value) =>
                fieldChangedCallback("aboutMe", name, value, false)
              }
              registration={false}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
              getAIBio={getAIBio}
              gptBusy={gptBusy}
            />
          </Form>
        </div>
      )}
      {tab === "pro-credentials" && (
        <div className={styles["edit-profile__body"]}>
          <Form
            noValidate
            name="proCredentials"
            validated={state.proCredentialsValidated}
            onSubmit={handleSubmit}
            id="proCredentials"
          >
            <ProTypeUpdate
              reset={() => null}
              data={state.proCredentialsData}
              fieldChangedCallback={(name, value, multi) =>
                fieldChangedCallback("proCredentials", name, value, false)
              }
              registration={false}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
            <ProCredentials
              data={state.proCredentialsData}
              fieldChangedCallback={(name, value) =>
                fieldChangedCallback("proCredentials", name, value, false)
              }
              registration={false}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
          </Form>
        </div>
      )}
      {tab === "service" && (
        <div className={styles["edit-profile__body"]}>
          <Form
            noValidate
            name="service"
            validated={state.serviceValidated}
            onSubmit={handleSubmit}
            id="service"
          >
            <Service
              data={state.serviceData}
              fieldChangedCallback={(name, value) =>
                fieldChangedCallback("service", name, value, false)
              }
              registration={false}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
          </Form>
        </div>
      )}
      {tab === "education-training" && (
        <div className={styles["edit-profile__body"]}>
          <Form
            noValidate
            name="educationTraining"
            validated={state.educationTrainingValidated}
            onSubmit={handleSubmit}
            id="educationTraining"
          >
            <EducationTraining
              data={state.educationTrainingData}
              fieldChangedCallback={(name, value) =>
                fieldChangedCallback("educationTraining", name, value, false)
              }
              registration={false}
              amplitudePageName={AMPLITUDE_PAGE_NAME}
            />
          </Form>
        </div>
      )}

      <div className={styles["edit-profile__footer"]}>
        <div className={"gray01 rg-12"}>
          ©2026 Orchid Exchange Inc. All rights reserved
        </div>
        <Button
          variant="primary"
          type="submit"
          className={styles["edit-profile__save-button"]}
          disabled={!changed}
          form={getFormIdByTab(tab)}
          onClick={() =>
            track(AMPLITUDE_PAGE_NAME, "settings-save-button", "click")
          }
        >
          Save
        </Button>
      </div>
    </div>
  );
};

export default EditProfile;
